import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { enrollmentService } from '../services/api';
import { Modal, Button, Spinner, EmptyState } from './ui';

const STATUS_OPTIONS = [
  { value: 'present', label: 'Present', tone: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  { value: 'absent', label: 'Absent', tone: 'bg-red-100 text-red-700 border-red-300' },
  { value: 'late', label: 'Late', tone: 'bg-amber-100 text-amber-700 border-amber-300' },
  { value: 'excused', label: 'Excused', tone: 'bg-gray-100 text-gray-700 border-gray-300' },
];

export default function EventAttendanceModal({ event, onClose }) {
  const { t } = useTranslation();
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [drafts, setDrafts] = useState({});

  const load = useCallback(() => {
    if (!event) return;
    setLoading(true);
    enrollmentService.getEventAttendance(event.id)
      .then((res) => {
        const data = res.data || [];
        setRoster(data);
        const initial = {};
        data.forEach((r) => { initial[r.student.id] = { status: r.status || 'present', notes: r.notes || '' }; });
        setDrafts(initial);
      })
      .catch(() => setRoster([]))
      .finally(() => setLoading(false));
  }, [event]);

  useEffect(() => { load(); }, [load]);

  const setStatus = (studentId, status) => {
    setDrafts((prev) => ({ ...prev, [studentId]: { ...prev[studentId], status } }));
  };

  const setNote = (studentId, notes) => {
    setDrafts((prev) => ({ ...prev, [studentId]: { ...prev[studentId], notes } }));
  };

  const markAll = (status) => {
    setDrafts((prev) => {
      const next = { ...prev };
      roster.forEach((r) => { next[r.student.id] = { ...next[r.student.id], status }; });
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const records = roster.map((r) => ({
        student: r.student.id,
        status: drafts[r.student.id]?.status || 'present',
        notes: drafts[r.student.id]?.notes || '',
      }));
      await enrollmentService.markEventAttendance(event.id, records);
      onClose();
    } catch (e) {
      console.error('Failed to save attendance', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={!!event}
      onClose={onClose}
      title={t('Take Attendance')}
      size="lg"
      footer={<>
        <Button variant="secondary" onClick={onClose}>{t('Cancel')}</Button>
        <Button loading={saving} onClick={save} disabled={roster.length === 0}>{t('Save Attendance')}</Button>
      </>}
    >
      {event && <p className="text-sm text-gray-500 mb-4">{event.title} · {new Date(event.start_datetime).toLocaleDateString()}</p>}

      {loading ? (
        <div className="py-8 flex justify-center"><Spinner /></div>
      ) : roster.length === 0 ? (
        <EmptyState title={t('No students enrolled yet')} />
      ) : (
        <>
          <div className="flex gap-2 mb-3">
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => markAll(opt.value)}
                className="text-xs px-2.5 py-1 rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50"
              >
                {t('Mark all {{status}}', { status: t(opt.label) })}
              </button>
            ))}
          </div>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {roster.map((r) => {
              const draft = drafts[r.student.id] || { status: 'present', notes: '' };
              return (
                <div key={r.student.id} className="border border-gray-100 rounded-lg p-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="font-medium text-gray-900 text-sm">
                      {r.student.first_name} {r.student.last_name}
                    </div>
                    <div className="flex gap-1.5">
                      {STATUS_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => setStatus(r.student.id, opt.value)}
                          className={`text-xs px-2.5 py-1 rounded-full border transition ${
                            draft.status === opt.value ? opt.tone : 'bg-white text-gray-400 border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          {t(opt.label)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <input
                    type="text"
                    placeholder={t('Note (optional)')}
                    value={draft.notes}
                    onChange={(e) => setNote(r.student.id, e.target.value)}
                    className="mt-2 w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5"
                  />
                </div>
              );
            })}
          </div>
        </>
      )}
    </Modal>
  );
}
